import { z } from 'zod'
import { utcMonthStart, utcNextMonthStart } from './ai-suggestions.ts'
import { sermonFileKind } from './sermon-files.ts'

/**
 * AI credits (docs/15-MUSIC-AND-AI.md, "AI credits"): what a church's plan
 * includes each month, and what each AI action costs. A credit is a product
 * unit, not tokens: platform operators price each action in credits (the
 * `ai_actions` table, over the defaults here) and give each billing plan its
 * monthly credits (the `ai_credits` limit). What a call really cost stays in
 * `ai_usage`, for the operators' margin.
 *
 * The pure parts: which actions exist, a church's balance this month, what
 * it reads when it is short. Credits are whole numbers, always.
 */

// ---- the actions ----------------------------------------------------------------------

/**
 * Every AI action code can run, with its defaults. Code decides which exist
 * (each is charged at one place in the API); operators set their credits,
 * whether they run, and their description. An action missing from the table
 * costs its default.
 */
export const AI_ACTIONS = [
  {
    key: 'plan_suggestions',
    name: 'Song suggestions for a plan',
    description: 'Reading a plan’s sermon and suggesting songs for it.',
    credits: 1,
  },
  {
    key: 'plan_suggestions_regenerate',
    name: 'Regenerate suggestions',
    description: 'Different songs for a sermon already read.',
    credits: 1,
  },
  {
    key: 'sermon_transcription',
    name: 'Sermon recording to text',
    description: 'A recorded sermon turned into text before it is read.',
    credits: 2,
  },
  {
    key: 'stream_clips',
    name: 'Clips from a recording',
    description:
      'Five 30–60 second highlights for Shorts and Reels, from a recording’s transcript.',
    credits: 1,
  },
  {
    key: 'stream_transcription',
    name: 'Recording to text, per hour',
    description:
      'A ForStream recording with no transcript yet, turned into text for clips: per started hour.',
    credits: 2,
  },
  // ---- sermon writing (docs/15, "Sermon writing"): one each, written from a read sermon ----
  {
    key: 'group_guide',
    name: 'Small-group guide',
    description: 'A discussion guide for small groups, from a plan’s sermon.',
    credits: 1,
  },
  {
    key: 'sermon_recap',
    name: 'Sermon recap',
    description: 'A newsletter paragraph, social captions and a pull quote, from a plan’s sermon.',
    credits: 1,
  },
  {
    key: 'family_devotional',
    name: 'Kids & family',
    description: 'A five-day family devotional and a kids’ explanation, from a plan’s sermon.',
    credits: 1,
  },
  {
    key: 'service_flow',
    name: 'Service flow writing',
    description: 'A call to worship, prayers, transitions and a benediction for a plan’s order.',
    credits: 1,
  },
  {
    key: 'slide_deck',
    name: 'Slide deck',
    description: 'A plan’s sermon as slides: its key points and scripture references.',
    credits: 2,
  },
  {
    key: 'series_plan',
    name: 'Series outline',
    description: 'A sermon series outlined from a book of the Bible or a topic.',
    credits: 1,
  },
  // ---- AI creative (docs/17-AI-CREATIVE.md) ----
  {
    key: 'series_art',
    name: 'Series art',
    description: 'Three directions for a series’ art, each with its main graphic.',
    credits: 15,
  },
  {
    key: 'series_art_more',
    name: 'More series art directions',
    description: 'Three more directions, different from the ones already made.',
    credits: 15,
  },
  {
    key: 'series_art_background',
    name: 'Slide background for series art',
    description: 'A text-free slide background matching one direction’s main graphic.',
    credits: 5,
  },
  {
    key: 'quote_cards',
    name: 'Quote cards',
    description: 'Three quotes, points or verses from a sermon, on a new background.',
    credits: 3,
  },
  {
    key: 'quote_cards_series_background',
    name: 'Quote cards on the series background',
    description: 'Three quotes, points or verses from a sermon, on its series’ slide background.',
    credits: 1,
  },
  {
    key: 'quote_background',
    name: 'New quote-card background',
    description: 'Another text-free background for a plan’s quote cards.',
    credits: 2,
  },
] as const satisfies readonly {
  key: string
  name: string
  description: string
  credits: number
}[]

/**
 * Actions code no longer runs, by key, with the name their old charges are
 * shown under (this month's use, the console). Song themes became automatic
 * and free (docs/15, "Song themes from the shared catalog"): ForHim pays.
 */
export const RETIRED_AI_ACTIONS: Readonly<Record<string, string>> = {
  song_themes: 'Themes for the song library',
}

export type AiActionKey = (typeof AI_ACTIONS)[number]['key']
export const AI_ACTION_KEYS = AI_ACTIONS.map((a) => a.key) as readonly AiActionKey[]
export const isAiActionKey = (value: string): value is AiActionKey =>
  (AI_ACTION_KEYS as readonly string[]).includes(value)

/** An action as it is priced now: the operators' row over the code's default. */
export interface AiAction {
  key: AiActionKey
  name: string
  description: string
  credits: number
  enabled: boolean
  position: number
}

/** The operators' values (rows of `ai_actions`), each action's default where it has none. */
export function resolveAiActions(
  rows: readonly {
    key: string
    name: string
    description: string
    credits: number
    enabled: boolean
    position: number
  }[],
): AiAction[] {
  const byKey = new Map(rows.map((row) => [row.key, row]))
  return AI_ACTIONS.map((action, index): AiAction => {
    const row = byKey.get(action.key)
    return row
      ? {
          key: action.key,
          name: row.name,
          description: row.description,
          credits: Math.max(0, Math.trunc(row.credits)),
          enabled: row.enabled,
          position: row.position,
        }
      : { ...action, enabled: true, position: index }
  }).sort((a, b) => a.position - b.position)
}

/**
 * What reading a sermon costs: the suggestions it leads to, and a recording's
 * transcription first. Reading a document or notes is part of the suggestions.
 */
export function sermonReadActions(
  file: { contentType: string | null; fileName: string | null } | null,
): AiActionKey[] {
  return file && sermonFileKind(file.contentType, file.fileName) === 'audio'
    ? ['plan_suggestions', 'sermon_transcription']
    : ['plan_suggestions']
}

/** What these actions cost together, and whether every one of them runs now. */
export function costOf(
  actions: readonly { key: string; credits: number; enabled: boolean }[],
  keys: readonly AiActionKey[],
) {
  const chosen = keys.map((key) => actions.find((a) => a.key === key))
  return {
    credits: chosen.reduce((sum, action) => sum + (action?.credits ?? 0), 0),
    enabled: chosen.every((action) => action?.enabled !== false),
  }
}

/** "1 credit", "2 credits", "no credits". */
export function creditsLabel(credits: number): string {
  if (credits === 0) return 'no credits'
  return `${credits.toLocaleString('en-US')} ${credits === 1 ? 'credit' : 'credits'}`
}

// ---- the balance ----------------------------------------------------------------------

/**
 * A one-off grant from a platform operator. It adds its credits to every
 * month it is live in: from when it is given until it expires — without an
 * expiry, until the end of the month it was given in. (A lasting raise is
 * an override of the plan's `ai_credits`, not a grant.)
 */
export interface CreditGrant {
  credits: number
  createdAt: Date
  expiresAt: Date | null
}

/** When a grant stops counting. */
export function grantEndsAt(grant: Pick<CreditGrant, 'createdAt' | 'expiresAt'>): Date {
  return grant.expiresAt ?? utcNextMonthStart(grant.createdAt)
}

/** Does this grant count at `now`? */
export function grantIsLive(grant: CreditGrant, now: Date): boolean {
  return grant.createdAt <= now && grantEndsAt(grant) > now
}

export interface CreditCharge {
  credits: number
  createdAt: Date
  refundedAt: Date | null
  /**
   * Given back of a run that only partly failed (one image of three): what
   * the charge still costs is `credits - refundedCredits`. A whole refund is
   * `refundedAt` instead.
   */
  refundedCredits?: number
}

/** What a charge still costs: nothing once refunded, less what a partial refund gave back. */
export function chargedCredits(charge: CreditCharge): number {
  if (charge.refundedAt !== null) return 0
  const credits = Math.max(0, Math.trunc(charge.credits))
  return Math.max(0, credits - Math.max(0, Math.trunc(charge.refundedCredits ?? 0)))
}

/**
 * The share of a run's credits to give back when some of its parts failed
 * (images of a set of three): in proportion, rounded down for the church's
 * benefit only when everything failed (then all of it). Whole credits.
 */
export function refundShare(credits: number, parts: number, failed: number): number {
  const whole = Math.max(0, Math.trunc(credits))
  if (parts <= 0 || failed <= 0) return 0
  if (failed >= parts) return whole
  return Math.min(whole, Math.round((whole * failed) / parts))
}

export interface CreditBalance {
  /** The plan's monthly credits (overrides applied); null: unlimited. */
  included: number | null
  /** Live grants this month. */
  bonus: number
  /** Charged this month, refunds given back. */
  used: number
  /** What is left now (never below 0); null: unlimited. */
  left: number | null
  /** included + bonus; null: unlimited. */
  total: number | null
  /** The first of next month (ISO date): when `used` starts again from 0. */
  resetsOn: string
  /** How it is running: plenty, low (a fifth or less left), out. */
  tone: 'ok' | 'low' | 'out'
}

/**
 * A church's credits this month (calendar month, UTC): the plan's monthly
 * credits, plus live grants, less this month's charges that were not
 * refunded. Charges and grants outside the month are ignored here, so the
 * caller may pass more than this month's.
 */
export function creditBalance(input: {
  included: number | null
  grants: readonly CreditGrant[]
  charges: readonly CreditCharge[]
  now: Date
}): CreditBalance {
  const start = utcMonthStart(input.now)
  const resetsOn = utcNextMonthStart(input.now).toISOString().slice(0, 10)
  const bonus = input.grants
    .filter((grant) => grantIsLive(grant, input.now))
    .reduce((sum, grant) => sum + Math.max(0, Math.trunc(grant.credits)), 0)
  const used = input.charges
    .filter((charge) => charge.createdAt >= start)
    .reduce((sum, charge) => sum + chargedCredits(charge), 0)
  if (input.included === null)
    return { included: null, bonus, used, left: null, total: null, resetsOn, tone: 'ok' }
  const included = Math.max(0, Math.trunc(input.included))
  const total = included + bonus
  const left = Math.max(0, total - used)
  const tone = left === 0 ? 'out' : left * 5 <= total ? 'low' : 'ok'
  return { included, bonus, used, left, total, resetsOn, tone }
}

/** Can the church pay for this now? */
export const canAfford = (balance: CreditBalance, credits: number) =>
  balance.left === null || balance.left >= credits

/** "November 1". */
export function resetDay(resetsOn: string): string {
  return new Date(`${resetsOn}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/** "37 of 50 left this month"; "Unlimited this month". */
export function balanceLine(balance: CreditBalance): string {
  if (balance.left === null || balance.total === null) return 'Unlimited this month'
  return `${balance.left.toLocaleString('en-US')} of ${balance.total.toLocaleString('en-US')} left this month`
}

/** What a person reads when the church can't pay for an action. */
export function shortOfCreditsMessage(
  what: string,
  credits: number,
  balance: CreditBalance,
): string {
  const left = balance.left ?? 0
  return `${what} costs ${creditsLabel(credits)} and your church has ${left === 0 ? 'none' : creditsLabel(left)} left this month. Credits reset on ${resetDay(balance.resetsOn)}.`
}

/**
 * Why a button that costs credits is off, in one short line under it:
 * "Costs 1 credit, and none are left this month. Credits reset on November 1."
 */
export function shortLine(credits: number, balance: CreditBalance): string {
  const left = balance.left ?? 0
  return `Costs ${creditsLabel(credits)}, and ${left === 0 ? 'none are' : `only ${creditsLabel(left)} ${left === 1 ? 'is' : 'are'}`} left this month. Credits reset on ${resetDay(balance.resetsOn)}.`
}

/**
 * The Billing card's bar: how much is left, as a share of the month's
 * credits (0–100), and its tone; null when unlimited.
 */
export function creditMeter(balance: CreditBalance): number | null {
  if (balance.left === null || balance.total === null) return null
  if (balance.total === 0) return 0
  return Math.max(0, Math.min(100, Math.round((balance.left / balance.total) * 100)))
}

/** A switched-off action, in words. */
export const actionOffMessage = (what: string) =>
  `${what} is switched off for now. Try again later.`

/** Credits used this month, by action, for the Billing card ("Song suggestions · 9 credits"). */
export function creditsByAction(
  charges: readonly (CreditCharge & { action: string })[],
  actions: readonly AiAction[],
  now: Date,
) {
  const start = utcMonthStart(now)
  const totals = new Map<string, { credits: number; times: number }>()
  for (const charge of charges) {
    if (charge.refundedAt !== null || charge.createdAt < start) continue
    const total = totals.get(charge.action) ?? { credits: 0, times: 0 }
    total.credits += chargedCredits(charge)
    total.times++
    totals.set(charge.action, total)
  }
  // A retired action's charges this month still count, under its old name.
  const named: { key: string; name: string }[] = [
    ...actions,
    ...Object.entries(RETIRED_AI_ACTIONS).map(([key, name]) => ({ key, name })),
  ]
  return named
    .flatMap((action) => {
      const total = totals.get(action.key)
      return total ? [{ key: action.key, name: action.name, ...total }] : []
    })
    .sort((a, b) => b.credits - a.credits)
}

/**
 * The operators' margin: what a credit really cost (micro-dollars per
 * credit, rounded), or null when nothing was charged.
 */
export function microsPerCredit(costMicros: number, credits: number): number | null {
  return credits > 0 ? Math.round(costMicros / credits) : null
}

// ---- the console's forms ---------------------------------------------------------------

export const AI_ACTION_MAX_CREDITS = 1000
export const AI_GRANT_MAX_CREDITS = 100_000

const wholeCredits = (min: number, max: number) =>
  z.coerce
    .number<string | number>()
    .int('A whole number')
    .min(min, `At least ${min}`)
    .max(max, `At most ${max.toLocaleString('en-US')}`)

/** An action's panel in the platform console. */
export const aiActionSchema = z.object({
  credits: wholeCredits(0, AI_ACTION_MAX_CREDITS),
  enabled: z.boolean(),
  description: z.string().trim().max(300, 'At most 300 characters'),
})
export type AiActionInput = z.output<typeof aiActionSchema>
export type AiActionFormInput = z.input<typeof aiActionSchema>

/** Grant credits (the platform console's church page). `expiresOn` is a date, or empty. */
export const aiGrantSchema = z.object({
  credits: wholeCredits(1, AI_GRANT_MAX_CREDITS),
  reason: z.string().trim().min(5, 'Say why, in a few words').max(500, 'At most 500 characters'),
  expiresOn: z
    .string()
    .trim()
    .refine((value) => value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value), 'Choose a date'),
})
export type AiGrantInput = z.output<typeof aiGrantSchema>
export type AiGrantFormInput = z.input<typeof aiGrantSchema>

/** A grant's expiry date (a day) as the moment it ends: the end of that day, UTC. */
export const grantExpiry = (expiresOn: string): string | null =>
  expiresOn ? new Date(`${expiresOn}T23:59:59Z`).toISOString() : null
